import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaUser, FaTimes, FaSpinner } from "react-icons/fa";
import { partners } from "../../../services/api";

const AssignPartnerModal = ({ 
  isOpen, 
  onClose, 
  rental, 
  onAssign 
}) => {
  const [partnersList, setPartnersList] = useState([]);
  const [selectedPartnerId, setSelectedPartnerId] = useState("");
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Fetch partners when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchPartners();
    }
  }, [isOpen]);

  const fetchPartners = async () => {
    try {
      setLoading(true);
      const response = await partners.getAll();
      setPartnersList(response.data);
    } catch (error) {
      console.error("Error fetching partners:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedPartnerId) return;

    try {
      setAssigning(true);
      await onAssign(rental._id, selectedPartnerId);
      onClose();
      setSelectedPartnerId("");
    } catch (error) {
      console.error("Error assigning partner:", error);
    } finally {
      setAssigning(false);
    }
  };

  const handleClose = () => {
    setSelectedPartnerId("");
    setSearchTerm("");
    onClose();
  };

  // Filter partners based on search term
  const filteredPartners = partnersList.filter(partner =>
    `${partner.firstName} ${partner.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    partner.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-lg p-6 w-full max-w-lg mx-4 max-h-[80vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-800">
              Assign Rental to Partner
            </h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <FaTimes className="w-5 h-5" />
            </button>
          </div>

          {/* Rental Info */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold text-gray-800 mb-2">Rental Details</h3>
            <div className="text-sm text-gray-600">
              <p><strong>Customer:</strong> {rental?.customer?.firstName} {rental?.customer?.lastName}</p>
              <p><strong>Car:</strong> {rental?.carName}</p>
              <p><strong>Total:</strong> €{rental?.totalPrice?.toFixed(2)}</p>
              {rental?.professionalPartner && (
                <p className="text-orange-600 mt-2">
                  <strong>Currently assigned to a partner</strong>
                </p>
              )}
            </div>
          </div>

          {/* Search */}
          <div className="mb-4">
            <input
              type="text"
              placeholder="Search partners..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Partners List */}
          <div className="mb-6">
            <h3 className="font-semibold text-gray-800 mb-3">Select Partner</h3>
            
            {loading ? (
              <div className="flex justify-center items-center py-8">
                <FaSpinner className="w-6 h-6 text-blue-500 animate-spin" />
              </div>
            ) : filteredPartners.length === 0 ? (
              <p className="text-gray-500 text-center py-4">
                {searchTerm ? "No partners found matching your search" : "No partners available"}
              </p>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {filteredPartners.map((partner) => (
                  <div
                    key={partner._id}
                    className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                      selectedPartnerId === partner._id
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                    onClick={() => setSelectedPartnerId(partner._id)}
                  >
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <FaUser className="w-4 h-4 text-blue-600" />
                        </div>
                      </div>
                      <div className="ml-3 flex-1">
                        <div className="font-medium text-gray-900">
                          {partner.firstName} {partner.lastName}
                        </div>
                        <div className="text-sm text-gray-500">
                          {partner.email} • {partner.commissionRate}% commission
                        </div>
                      </div>
                      {selectedPartnerId === partner._id && (
                        <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                          <div className="w-2 h-2 bg-white rounded-full" />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleAssign}
              disabled={!selectedPartnerId || assigning}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {assigning && <FaSpinner className="w-4 h-4 animate-spin" />}
              Assign Partner
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default AssignPartnerModal;
